import React, { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import "./styles.css";
import App from "./App";
import ErrorBoundary from "./components/ErrorBoundary";
import * as serviceWorkerRegistration from './lib/serviceWorkerRegistration';
import { startStatusBar } from './lib/statusBar';
import { startTapLog } from './lib/tapLog';
import { captureInstallPrompt } from './lib/installApp';

// La barra di stato: il blu dello splash (un meta solo) e gli ascoltatori,
// prima di tutto il resto (lib/statusBar).
startStatusBar();
// Il registro dei tocchi (pannello nascosto: 5 tocchi sullo stemma della Home).
startTapLog();

// Versioning
const APP_VERSION = '1.2.7';

// "Installa l'app": Chrome manda beforeinstallprompt presto e una volta sola,
// quindi lo si prende qui, prima di React (lib/installApp). Il foglio e quando
// mostrarlo stanno in components/install.
captureInstallPrompt();

// Cache management functions
const clearCache = async () => {
  try {
    if ('caches' in window) {
      const cacheKeys = await caches.keys();
      await Promise.all(
        cacheKeys.map(key => caches.delete(key))
      );
    }
  } catch (e) {
    console.warn('[NVA] clearCache failed:', e);
  }
};

const checkForUpdates = async () => {
  try {
    const storedVersion = localStorage.getItem('app-version');
    if (storedVersion !== APP_VERSION) {
      console.log('New version detected, clearing cache...');
      await clearCache();
      localStorage.setItem('app-version', APP_VERSION);
      return true;
    }
  } catch (e) {
    console.warn('[NVA] checkForUpdates failed:', e);
  }
  return false;
};

// ---- Aggiornamento dell'app senza interrompere l'ospite ----
// La versione nuova del service worker resta "in attesa" (il worker non fa
// più skipWaiting da solo). La pagina la attiva in due momenti sicuri: appena
// avviata (primi secondi: non c'è ancora niente da perdere) oppure quando
// l'app va in background. Mai mentre l'ospite la sta usando: il reload a
// metà chatbot o a metà modulo, pochi secondi dopo ogni deploy, era il
// difetto di prima. Il reload vero lo fa `controllerchange`, quando il
// worker nuovo ha davvero preso il controllo.
const PAGE_LOADED_AT = Date.now();
const JUST_LAUNCHED_MS = 5_000;
let swUpdatePending = false;

const activateWaitingWorker = async (registration: ServiceWorkerRegistration) => {
  try {
    const waiting = registration.waiting;
    if (!waiting) return;
    swUpdatePending = false;
    // Niente svuotamento delle cache qui: la versione in attesa ha GIÀ
    // riempito la sua (guscio, offline.html, tutti i file della build) e le
    // cache vecchie le toglie lei quando si attiva. Svuotarle adesso voleva
    // dire che il reload subito dopo, se offline (app in background senza
    // campo), trovava "Offline page not found" per sempre, e che i tour
    // audio scaricati sparivano a ogni aggiornamento.
    waiting.postMessage({ type: 'SKIP_WAITING' });
  } catch (e) {
    console.warn('[NVA] activateWaitingWorker failed:', e);
  }
};

const handleServiceWorkerUpdate = (registration: ServiceWorkerRegistration) => {
  if (!registration.waiting) return;
  const justLaunched = Date.now() - PAGE_LOADED_AT < JUST_LAUNCHED_MS;
  if (justLaunched || document.visibilityState === 'hidden') {
    void activateWaitingWorker(registration);
    return;
  }
  swUpdatePending = true;
};

// Utility functions
const isIOS = () => {
  if (typeof window !== 'undefined') {
    const userAgent = window.navigator.userAgent.toLowerCase();
    return /iphone|ipad|ipod/.test(userAgent);
  }
  return false;
};

const hasRedirectParam = () => {
  if (typeof window === 'undefined') return false;
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.has('pwa-redirect') && 
         (Date.now() - Number(urlParams.get('t') || 0)) < 5000;
};

const isInStandaloneMode = () => 
  typeof window !== 'undefined' && 
  (window.matchMedia('(display-mode: standalone)').matches || 
   window.matchMedia('(display-mode: fullscreen)').matches || 
   window.matchMedia('(display-mode: minimal-ui)').matches || 
   (isIOS() && (window.navigator as any).standalone));

const checkActualInstallation = async () => {
  if (isInStandaloneMode()) {
    return true;
  }

  if ('getInstalledRelatedApps' in navigator) {
    try {
      // @ts-ignore
      const relatedApps = await navigator.getInstalledRelatedApps();
      if (relatedApps.length > 0) {
        return true;
      }
    } catch (e) {
      console.log('GetInstalledRelatedApps check failed:', e);
    }
  }

  try {
    return localStorage.getItem('pwa-installed') === 'true';
  } catch (e) {
    return false;
  }
};

const isPWAInstalled = async () => {
  if (typeof window === 'undefined') return false;
  if (hasRedirectParam()) return false;
  
  return await checkActualInstallation();
};

// PWA installation and redirection handling
if (typeof window !== 'undefined') {
  const originalUrl = window.location.href;
  
  const attemptPWARedirect = async () => {
    if (isInStandaloneMode()) {
      return;
    }

    const isInstalled = await isPWAInstalled();
    if (!isInstalled) {
      return;
    }

    // Da Safari non si può aprire l'app installata: ricaricare servirebbe solo
    // a perdere la pagina (e i suoi parametri, es. ?add= della gift card).
    if (isIOS()) return;

    // Redirect WebAPK/PWA standalone tramite parametri (no intent verso TWA
    // legacy), tenendo quelli che la pagina aveva già.
    const pwaUrl = new URL(originalUrl);
    pwaUrl.searchParams.set('pwa-redirect', 'true');
    pwaUrl.searchParams.set('t', Date.now().toString());

    if (!hasRedirectParam()) {
      try {
        window.location.replace(pwaUrl.toString());
      } catch (e) {
        window.location.href = pwaUrl.toString();
      }
    }
  };

  // I vecchi banner (Android subito e ogni 60 secondi, iPhone a ogni apertura)
  // sono diventati il foglio components/install/InstallAppSheet.

  // Event listener per l'installazione completata
  window.addEventListener('appinstalled', async () => {
    try {
      localStorage.setItem('pwa-installed', 'true');
      localStorage.setItem('app-version', APP_VERSION);
    } catch (e) {}
    console.log('PWA installed successfully');
    setTimeout(async () => {
      await attemptPWARedirect();
    }, 2000);
  });

  // iOS specific handlers
  if (isIOS()) {
    window.addEventListener('load', async () => {
      try {
        if ((window.navigator as any).standalone) {
          try { localStorage.setItem('pwa-installed', 'true'); } catch (e) {}
          // Force version check on iOS PWA load
          const needsUpdate = await checkForUpdates();
          if (needsUpdate) {
            console.log('[iOS PWA] Version mismatch, reloading...');
            window.location.reload();
          }
        }
      } catch (e) { console.warn('[NVA] iOS load handler error:', e); }
    });

    document.addEventListener('visibilitychange', async () => {
      try {
      if (document.visibilityState === 'visible') {
        if ((window.navigator as any).standalone) {
          try { localStorage.setItem('pwa-installed', 'true'); } catch (e) {}
          // Force version check when iOS PWA comes to foreground
          let storedVersion: string | null = null;
          try { storedVersion = localStorage.getItem('app-version'); } catch (e) {}
          if (storedVersion !== APP_VERSION) {
            console.log('[iOS PWA] New version detected on resume, clearing cache...');
            await clearCache();
            try { localStorage.setItem('app-version', APP_VERSION); } catch (e) {}
            window.location.reload();
            return;
          }
        }
        setTimeout(async () => {
          await attemptPWARedirect();
        }, 1000);
      }
      } catch (e) { console.warn('[NVA] iOS visibilitychange handler error:', e); }
    });
  }
}

// Service Worker registration
if (typeof window !== 'undefined' && window.location.hostname !== 'localhost') {
  if ('serviceWorker' in navigator) {
    // Al primo avvio nessun worker controlla la pagina: quando il primo prende
    // il controllo non serve ricaricare (la pagina è appena arrivata dalla
    // rete). Il reload serve solo al CAMBIO di worker.
    const hadControllerAtLoad = !!navigator.serviceWorker.controller;

    navigator.serviceWorker.ready.then(registration => {
      // Versione scaricata in un'apertura precedente e rimasta in attesa.
      if (registration.waiting) handleServiceWorkerUpdate(registration);
      registration.addEventListener('updatefound', () => {
        const newWorker = registration.installing;
        if (newWorker) {
          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              handleServiceWorkerUpdate(registration);
            }
          });
        }
      });
    });

    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (!hadControllerAtLoad) return;
      window.location.reload();
    });

    // L'app va in background: il momento buono per applicare la versione in attesa.
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden' && swUpdatePending) {
        navigator.serviceWorker.ready.then(activateWaitingWorker).catch(() => {});
      }
    });
  }

  serviceWorkerRegistration.register();

  window.addEventListener('online', async () => {
    if ('serviceWorker' in navigator) {
      const registration = await navigator.serviceWorker.ready;
      // Offline (o server giù) fallisce: niente errori non gestiti.
      registration.update().catch(() => {});
    }
  });

  document.addEventListener('visibilitychange', async () => {
    if (document.visibilityState === 'visible' && 'serviceWorker' in navigator) {
      const registration = await navigator.serviceWorker.ready;
      registration.update().catch(() => {});
    }
  });
}

// Lo scorrimento lo rimette l'app, non il browser: qui scorre un contenitore
// dentro la pagina, e il tentativo automatico del browser farebbe solo il
// verso a quello vero (vedi hooks/useScrollRestoration).
if ('scrollRestoration' in window.history) {
  window.history.scrollRestoration = 'manual';
}

// App rendering
const rootElement = document.getElementById("root");
if (rootElement) {
  const root = createRoot(rootElement);
  root.render(
    <StrictMode>
      <ErrorBoundary>
        <App />
      </ErrorBoundary>
    </StrictMode>
  );
} else {
  console.error("Root element not found in DOM");
}

